/**
 * mobile-segments.ts — Logic thuần của thanh phân đoạn tuỳ chỉnh ở Hộp thư di động ("Trò chuyện · Chưa đọc · Nhóm" mặc định).
 *
 * Mỗi phân đoạn là một bộ lọc có thật của Hộp thư (`ConvFilter`) hoặc một Nhãn khách (`tag:<tên>`). Người dùng chọn / sắp xếp / ẩn
 * trong hộp "Sửa"; danh sách lưu theo người dùng trong localStorage. Luôn còn ít nhất một phân đoạn. Phần giao diện: mobile-inbox-header.tsx,
 * mobile-segments-sheet.tsx.
 */
import type { ConvFilter } from './filter-rail'
import { i18n } from '@/lib/i18n'
import type { ConversationCounts } from '@/hooks/use-conversations'

/** Id phân đoạn: một bộ lọc (`unread`…) hoặc `tag:<tên nhãn>`. */
export type SegmentId = string

export const TAG_PREFIX = 'tag:'
export const tagSegmentId = (name: string): SegmentId => `${TAG_PREFIX}${name}`
/** Tên nhãn của phân đoạn nhãn; không phải phân đoạn nhãn → undefined. */
export function tagOfSegment(id: SegmentId): string | undefined {
  return id.startsWith(TAG_PREFIX) && id.length > TAG_PREFIX.length ? id.slice(TAG_PREFIX.length) : undefined
}

export interface SegmentDef {
  filter: ConvFilter
  /** Nhãn ngắn trên thanh. */
  label: string
  /** Mô tả trong hộp "Sửa". */
  hint: string
}

/** Các phân đoạn có sẵn (bỏ "Lưu trữ" — đổi cả phạm vi đếm, không hợp làm phân đoạn nhanh). */
export const SEGMENT_CATALOG: SegmentDef[] = [
  { filter: 'all', get label() { return i18n.t('convList:seg.all.label') }, get hint() { return i18n.t('convList:seg.all.hint') } },
  { filter: 'unread', get label() { return i18n.t('convList:seg.unread.label') }, get hint() { return i18n.t('convList:seg.unread.hint') } },
  { filter: 'unreplied', get label() { return i18n.t('convList:seg.unreplied.label') }, get hint() { return i18n.t('convList:seg.unreplied.hint') } },
  { filter: 'mine', get label() { return i18n.t('convList:seg.mine.label') }, get hint() { return i18n.t('convList:seg.mine.hint') } },
  { filter: 'unassigned', get label() { return i18n.t('convList:seg.unassigned.label') }, get hint() { return i18n.t('convList:seg.unassigned.hint') } },
  { filter: 'ai', get label() { return i18n.t('convList:seg.ai.label') }, get hint() { return i18n.t('convList:seg.ai.hint') } },
  { filter: 'pinned', get label() { return i18n.t('convList:seg.pinned.label') }, get hint() { return i18n.t('convList:seg.pinned.hint') } },
  { filter: 'direct', get label() { return i18n.t('convList:seg.direct.label') }, get hint() { return i18n.t('convList:seg.direct.hint') } },
  { filter: 'group', get label() { return i18n.t('convList:seg.group.label') }, get hint() { return i18n.t('convList:seg.group.hint') } },
]

export const DEFAULT_SEGMENTS: SegmentId[] = ['all', 'unread', 'group']

const CATALOG_BY_FILTER = new Map<string, SegmentDef>(SEGMENT_CATALOG.map((d) => [d.filter, d]))
export const isFilterSegment = (id: SegmentId): boolean => CATALOG_BY_FILTER.has(id)

/** Nhãn hiển thị của phân đoạn ('' nếu không nhận ra). */
export function segmentLabel(id: SegmentId): string {
  const tag = tagOfSegment(id)
  if (tag) return tag
  return CATALOG_BY_FILTER.get(id)?.label ?? ''
}

/**
 * Làm sạch danh sách đọc từ bộ nhớ / cấu hình: chỉ giữ chuỗi hợp lệ, bỏ trùng, giữ thứ tự; rỗng / hỏng → mặc định.
 * `knownTags` (tên các nhãn hiện có): phân đoạn nhãn đã bị xoá thì bỏ; chưa biết (đang tải) → giữ nguyên.
 */
export function normalizeSegments(raw: unknown, knownTags?: string[]): SegmentId[] {
  if (!Array.isArray(raw)) return [...DEFAULT_SEGMENTS]
  const out: SegmentId[] = []
  for (const x of raw) {
    if (typeof x !== 'string' || out.includes(x)) continue
    const tag = tagOfSegment(x)
    if (tag) {
      if (knownTags && !knownTags.includes(tag)) continue
    } else if (!isFilterSegment(x)) continue
    out.push(x)
  }
  return out.length > 0 ? out : [...DEFAULT_SEGMENTS]
}

/** Bật / tắt một phân đoạn. Tắt cái cuối cùng → không đổi (luôn còn tối thiểu một). Bật → thêm vào cuối. */
export function toggleSegment(list: SegmentId[], id: SegmentId): SegmentId[] {
  if (list.includes(id)) return list.length <= 1 ? list : list.filter((x) => x !== id)
  return [...list, id]
}

/** Dời một phân đoạn lên (-1) / xuống (+1) một bậc; ở mép hoặc không có trong danh sách → không đổi. */
export function moveSegment(list: SegmentId[], id: SegmentId, dir: -1 | 1): SegmentId[] {
  const i = list.indexOf(id)
  const j = i + dir
  if (i < 0 || j < 0 || j >= list.length) return list
  const next = [...list]
  ;[next[i], next[j]] = [next[j], next[i]]
  return next
}

/** Thứ tự hiển thị trong hộp "Sửa": đang bật (theo thứ tự đã chọn) rồi tới các mục còn lại (bộ lọc có sẵn, sau đó nhãn). */
export function editorOrder(list: SegmentId[], tagNames: string[]): SegmentId[] {
  const all = [...SEGMENT_CATALOG.map((d) => d.filter as SegmentId), ...tagNames.map(tagSegmentId)]
  return [...list, ...all.filter((id) => !list.includes(id))]
}

/** Đích khi chọn phân đoạn: bộ lọc nào + nhãn nào (phân đoạn nhãn = bộ lọc "all" + nhãn). */
export function segmentTarget(id: SegmentId): { filter: ConvFilter; tag?: string } {
  const tag = tagOfSegment(id)
  return tag ? { filter: 'all', tag } : { filter: id as ConvFilter }
}

/** Phân đoạn có đang sáng không, theo trạng thái lọc hiện tại của trang. */
export function isSegmentActive(id: SegmentId, state: { filter: ConvFilter; tag?: string; viewActive?: boolean }): boolean {
  if (state.viewActive) return false
  const t = segmentTarget(id)
  return t.tag !== undefined ? state.tag === t.tag : !state.tag && state.filter === t.filter
}

/** Số hiển thị cạnh phân đoạn (0 = không hiện). Phân đoạn nhãn và "Trò chuyện" không có số. */
export function segmentCountOf(id: SegmentId, counts?: ConversationCounts): number {
  if (!counts || tagOfSegment(id)) return 0
  switch (id) {
    case 'unread': return counts.unread
    case 'unreplied': return counts.unreplied
    case 'mine': case 'unassigned': case 'ai': case 'pinned': case 'direct': case 'group':
      return counts.views?.[id] ?? 0
    default: return 0
  }
}

// ── Bản lưu: bộ nhớ đệm trên máy + đồng bộ với máy chủ ─────────────────────────

/** Danh sách + thời điểm người dùng sửa (ISO) — thời điểm quyết định bản nào mới hơn giữa các thiết bị. */
export interface SegmentsPref { segments: SegmentId[]; updatedAt: string }

/** Bản cũ (chỉ mảng, chưa có giờ) coi như rất cũ: bản máy chủ nào cũng thắng, nhưng nếu máy chủ chưa có thì vẫn được đẩy lên. */
export const LEGACY_STAMP = '1970-01-01T00:00:00.000Z'

export const segmentsStorageKey = (userId?: string | null) => `ecdp_mobile_segments:${userId || 'anon'}`

type KeyValueStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

const stampOf = (iso: unknown): number => (typeof iso === 'string' ? Date.parse(iso) : NaN)

/**
 * Làm sạch bản lấy từ máy chủ / bộ nhớ: phải có danh sách hợp lệ (mã lạ bị loại, trùng bị bỏ, hết sạch → coi như không có) và giờ đọc được.
 * Không trả về mặc định thay cho dữ liệu hỏng — "không có bản nào" khác "bản là mặc định".
 */
export function parsePref(raw: unknown): SegmentsPref | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const { segments, updatedAt } = raw as { segments?: unknown; updatedAt?: unknown }
  if (!Array.isArray(segments) || !Number.isFinite(stampOf(updatedAt))) return null
  const clean = normalizeSegments(segments.filter((x) => typeof x === 'string' && (isFilterSegment(x) || tagOfSegment(x) !== undefined)))
  // normalizeSegments trả mặc định khi rỗng — phân biệt bằng cách xem còn phần tử hợp lệ nào không.
  const any = segments.some((x) => typeof x === 'string' && (isFilterSegment(x) || tagOfSegment(x) !== undefined))
  return any ? { segments: clean, updatedAt: new Date(stampOf(updatedAt)).toISOString() } : null
}

export type MergeAction = 'none' | 'adopt-remote' | 'push-local'

/**
 * Hợp nhất bản trên máy (`local`, null = chưa từng tuỳ chỉnh) với bản máy chủ (`remote`, null = máy chủ chưa có):
 *  - cả hai trống → không làm gì;  máy chủ trống, máy này có → đẩy lên (lần đầu);  máy này trống → nhận bản máy chủ;
 *  - cả hai có → bản `updatedAt` mới hơn thắng; bằng nhau mà nội dung khác → theo máy chủ (một nguồn sự thật).
 */
export function mergePrefs(local: SegmentsPref | null, remote: SegmentsPref | null): { pref: SegmentsPref | null; action: MergeAction } {
  if (!remote) return local ? { pref: local, action: 'push-local' } : { pref: null, action: 'none' }
  if (!local) return { pref: remote, action: 'adopt-remote' }
  const tl = stampOf(local.updatedAt)
  const tr = stampOf(remote.updatedAt)
  if (tr > tl) return { pref: remote, action: 'adopt-remote' }
  if (tl > tr) return { pref: local, action: 'push-local' }
  return samePref(local, remote) ? { pref: local, action: 'none' } : { pref: remote, action: 'adopt-remote' }
}

export const samePref = (a: SegmentsPref, b: SegmentsPref) => a.segments.length === b.segments.length && a.segments.every((x, i) => x === b.segments[i])

/** Đọc bộ nhớ đệm trên máy: bản mới `{segments, updatedAt}`, hoặc bản cũ chỉ-mảng (→ LEGACY_STAMP). Hỏng / chưa có → null. */
export function loadPref(userId: string | null | undefined, store?: KeyValueStore): SegmentsPref | null {
  try {
    const raw = JSON.parse((store ?? localStorage).getItem(segmentsStorageKey(userId)) ?? 'null') as unknown
    return Array.isArray(raw) ? parsePref({ segments: raw, updatedAt: LEGACY_STAMP }) : parsePref(raw)
  } catch {
    return null
  }
}

/** Ghi bộ nhớ đệm. Cả bản "mặc định" cũng ghi (kèm giờ) — người dùng bấm "Mặc định" là một lựa chọn cần đồng bộ, không phải "chưa chọn". */
export function savePref(userId: string | null | undefined, pref: SegmentsPref, store?: KeyValueStore): void {
  try { (store ?? localStorage).setItem(segmentsStorageKey(userId), JSON.stringify(pref)) } catch { /* bộ nhớ bị chặn / đầy → bỏ qua, vẫn dùng được trong phiên */ }
}
